/**
 * THE READ REGISTRY — plan-025 R-DOM (design §36.5, §36.6; ADR 0077; glossary §6.40 "Account read", "Read projection").
 *
 * Every read the help assistant may make of a signed-in person's account, and nothing else. Each read code names one
 * EXISTING customer `GET` route (its operationId and path template), the arguments the model may give it, how they
 * become the route's path and query, and the projection the api applies to the route's answer before it leaves the
 * api. The help path adds no authorisation and removes none: the route's own checks decide (design §36.4), and the
 * parity suite (`apps/api/src/http/help-delegation.pg.test.ts`) is the gate.
 *
 * Arguments the MODEL gives are ids it read in an earlier result (a VoiceAgent, a conversation, a campaign, a SIP trunk,
 * a billing account) and a `period`. The organization's and the workspace's ids are NEVER the model's: they are the
 * grant's (`HelpAccountReadScope`), so a read cannot name another organization even by mistake.
 *
 * A projection (`projectHelpAccountRead`) is a pure function from the route's documented answer to a small object:
 * only the listed fields; lists cut at {@link HELP_ACCOUNT_READ_LIST_LIMIT} with the count left over; money as the
 * route's own minor units and currency plus a display text the screens print (`formatCreditMoney`) — never a sum or a
 * price of its own; text written by customers (names) kept and listed in `quoted_field`; text written by callers or
 * visitors (gap questions, transcript lines, messages) kept and marking the result `untrusted_text`; every string passed
 * through `scrubSecretShape` and cut at {@link HELP_ACCOUNT_READ_TEXT_CHAR_LIMIT}; carriers, providers, hosts, credentials,
 * addresses of others and rate tables never kept. The whole result is at most `HELP_ACCOUNT_READ_RESULT_CHAR_LIMIT`
 * characters, cut by whole list items.
 *
 * AMENDED 2026-10-28 (plan-030 HELP-Q; design §41.11; OD-10). `survey_questionnaire` reads a survey VoiceAgent's
 * questions (`getVoiceAgentQuestionnaireDocument`), so the assistant can read and explain them; it changes nothing. Its
 * projection keeps, per language, the group count and each question's code, type, wording, options' labels and range,
 * worked out from the route's `survey` (`withQuestionnaireOutline`). The wording is the organization's own text taken
 * from an uploaded document, so it is BOTH quoted and untrusted (`"quoted_untrusted"`): listed in `quoted_field`, and
 * the result marked `untrusted_text`. Never the code history, the language pack's words or wetalk's own lines.
 *
 * AMENDED 2026-10-28 (plan-031 VMD-HELP; design §42.11). The reads pass codes through, so `voicemail` reaches the
 * assistant as it is: as a conversation's `end_reason` (an outbound conversation a voicemail answered, hung up on at once
 * and billed until then) and as a campaign `outcome[].code` (retried like `no_answer`, never connected). The campaign read
 * also keeps `voicemail_count` and `voicemail_detection` (`off` | `shadow` | `on`, the platform's switch, so the assistant
 * never claims a detection that is off), and the analytics read `outbound` (answered outbound conversations and those a
 * voicemail answered).
 *
 * AMENDED 2026-10-28 (plan-032 HELP-OR; design §43.14; OD-8). Two R2 reads explain call reviews and change nothing:
 * `optimisation_reviews` (a VoiceAgent's reviews, newest first: state, depth, window, failure reason, finding count) and
 * `optimisation_review` (one review by the new id argument `optimisation_review_id`, found through the first: its
 * state, failure reason, conversations in the window and read, who pays, and per finding its position, category, fix
 * kind, advice target, decision, count and its words). The issue, the review's description and the fix are written by
 * the review from callers' words, so they are `"untrusted"` and mark the result `untrusted_text`; a finding's
 * `evidence_line` (the conversations' own lines) is never kept, and no amount is kept (the price is held, and charged
 * only when a review finds something: the ledger read says what was charged). No help action starts, accepts or
 * publishes a review. A runtime and an api built from this registry are deployed together.
 *
 * AMENDED 2026-10-28 (plan-033 HELP-RET; design §44.8, §44.11). The campaign read's `outcome[].code` reports a `partial`
 * attempt as `hung_up_mid_way` (at least one survey answer) or `hung_up_before_first_question` (nothing answered), and
 * `declined` is a line that was busy or a ring that was rejected (never a person who said no — that is a hang-up),
 * settled before plan-033 OD-23 (2026-10-28) and not called again; since then such a leg is `no_answer` and is retried. The
 * read also keeps `called_back_count`: people whose counted attempt is a call-back — they rang the campaign's number back,
 * while it ran or within a day after it finished, were counted under what happened on that conversation, and are not
 * dialled again.
 *
 * Imports only the domain's money formatter, the scrub and the limits, so the api and the runtime read one registry.
 */
import { formatCreditMoney } from "../billing/credit-figure.js";
import { HELP_ACCOUNT_READ_PERIOD, helpAccountReadWindowOf, isHelpCalendarDate, isHelpTimeZone, } from "./account-period.js";
import { HELP_ACCOUNT_READ_RESULT_CHAR_LIMIT } from "./limit.js";
import { scrubSecretShape } from "./scrub.js";
/** Items kept of any list in a result. */
export const HELP_ACCOUNT_READ_LIST_LIMIT = 20;
/** Characters kept of any one string in a result. */
export const HELP_ACCOUNT_READ_TEXT_CHAR_LIMIT = 500;
/** Lines kept of one conversation's transcript (plan-025 OD-3). */
export const HELP_ACCOUNT_TRANSCRIPT_LINE_LIMIT = 20;
/** The ids the model may pass back from earlier results. */
export const HELP_ACCOUNT_READ_ID_ARGUMENT = [
    "voice_agent_id",
    "conversation_id",
    "campaign_id",
    "sip_trunk_id",
    "billing_account_id",
    "workspace_member_person_id",
    "optimisation_review_id",
];
// ── periods (in the workspace's zone: ./account-period.ts) ─────────────────────────────────────
const sinceUntil = (window) => ({ since: window.start.toISOString(), until: window.end.toISOString() });
const fromTo = (window) => ({ from: window.start.toISOString(), to: window.end.toISOString() });
/** `listBillingUsage` takes calendar days, both inclusive: the workspace's own days (its billing day is taken in its zone). */
const fromToDay = (window) => ({ from: window.firstDay, to: window.lastDay });
/** `period=YYYY-MM` of the finance reads: the month the window starts in. */
const month = (window) => ({ period: window.firstMonth });
// ── shapes reused ───────────────────────────────────────────────────────────────────────────────
const AMOUNT = { amount_minor: true, currency: true };
const LIMIT = String(HELP_ACCOUNT_READ_LIST_LIMIT);
const VOICE_AGENT_SUMMARY = {
    voice_agent_id: true,
    name: "quoted",
    state: true,
    agent_template_code: true,
    agent_template_name: true,
    live_version_label: true,
    live_version_seq: true,
    language_code: [true],
    recording_enabled: true,
    conversation_count_today: true,
    billed_second_today: true,
    billed_amount_today: AMOUNT,
    billed_second_month: true,
    billed_amount_month: AMOUNT,
    paused_by: true,
    reachable: true,
    campaign_dialling: true,
};
const CONVERSATION_SUMMARY = {
    conversation_id: true,
    voice_agent_id: true,
    agent_version_label: true,
    medium: true,
    direction: true,
    is_test: true,
    campaign_id: true,
    // As the route answers it: masked for a role without `caller_detail.read`.
    party_address: true,
    language_code: true,
    started_at: true,
    answered_at: true,
    ended_at: true,
    billed_second: true,
    billed_amount: AMOUNT,
    outcome: true,
    // A code, passed through: caller protection's reasons, `voicemail` (plan-031: a voicemail answered an outbound
    // conversation and wetalk hung up at once), and every other `ConversationEndReason`.
    end_reason: true,
    has_taken_message: true,
};
const AUDIT_ENTRY = {
    data: [
        {
            occurred_at: true,
            actor_kind: true,
            actor_name: "quoted",
            action_code: true,
            event_class: true,
            summary: "quoted",
            origin: true,
            impersonated_by_operator_name: true,
            target_kind: true,
            result: true,
            help_action_id: true,
        },
    ],
    matched_count: true,
};
const NUMBER = {
    phone_number_id: true,
    e164: true,
    country: true,
    is_caller_id: true,
    sip_trunk_display_name: "quoted",
    enabled: true,
    voice_agent_id: true,
    voice_agent_name: "quoted",
    purchased_at: true,
    released_at: true,
    inbound_failover: true,
};
const SIP_TRUNK = {
    sip_trunk_id: true,
    display_name: "quoted",
    transport: true,
    media_encryption: true,
    password_set: true,
    state: true,
    inbound_readiness: { ready: true, reason: true },
    verified_at: true,
    disabled_at: true,
    created_at: true,
};
const CAMPAIGN = {
    campaign_id: true,
    name: "quoted",
    state: true,
    dialling_state: true,
    contact_list_name: "quoted",
    person_count: true,
    called_count: true,
    completed_count: true,
    starts_at: true,
    started_at: true,
    finished_at: true,
    estimated_finish_at: true,
};
// ── the registry ────────────────────────────────────────────────────────────────────────────────
export const HELP_ACCOUNT_READ = Object.freeze({
    // ── money, plan and usage (R1) ──
    balance: {
        operationId: "getBillingBalance",
        pathTemplate: "/v1/billing/balance",
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                billing_account_id: true,
                balance_minor: true,
                unbilled_today_minor: true,
                reserved_minor: true,
                available_minor: true,
                currency: true,
                trial_second_remaining: true,
                low_balance_talk_minute: true,
                hard_stop_enabled: true,
                state: true,
                computed_at: true,
                held_conversation_count: true,
                has_trial_grant: true,
            },
        },
    },
    funding: {
        operationId: "getWorkspaceFunding",
        pathTemplate: "/v1/billing/funding",
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                billing_account_id: true,
                display_name: "quoted",
                scope: true,
                since: true,
                billing_timezone: true,
                funded_workspace_count: true,
                funded_workspace: [{ workspace_name: "quoted" }],
                finance_access: true,
            },
        },
    },
    finance_overview: {
        operationId: "getFinanceOverview",
        // Joined from segments: the path-helper rule reads a tenant-id placeholder followed by a slash as a storage path.
        pathTemplate: ["/v1/accounts", "{account_id}", "finance-overview"].join("/"),
        wave: "r1",
        requiredId: [],
        period: month,
        fixedQuery: {},
        keep: {
            data: {
                time_zone: true,
                period: { month: true, start: true, end: true, in_progress: true },
                currency: true,
                total: { settled_minor: true, subscription_fee_minor: true, pending_minor: true, estimated_minor: true },
                billing_account: [
                    {
                        billing_account_id: true,
                        display_name: "quoted",
                        scope: true,
                        lifecycle_state: true,
                        collection_policy: true,
                        currency: true,
                        settled_minor: true,
                        subscription_fee_minor: true,
                        pending_minor: true,
                        pending_count: true,
                        estimated_minor: true,
                    },
                ],
            },
        },
    },
    ledger: {
        operationId: "listLedgerEntries",
        pathTemplate: "/v1/billing/ledger",
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: { limit: "10" },
        keep: {
            data: [
                {
                    occurred_at: true,
                    kind: true,
                    description: true,
                    voice_agent_id: true,
                    usage_day: true,
                    billed_second: true,
                    amount_minor: true,
                    currency: true,
                    balance_after_minor: true,
                },
            ],
        },
    },
    usage: {
        operationId: "listBillingUsage",
        pathTemplate: "/v1/billing/usage",
        wave: "r1",
        requiredId: [],
        period: fromToDay,
        fixedQuery: {},
        keep: {
            data: [
                {
                    voice_agent_id: true,
                    usage_day: true,
                    medium: true,
                    is_test: true,
                    billed_second: true,
                    conversation_count: true,
                    charged_minor: true,
                    currency: true,
                    closed: true,
                    today: true,
                },
            ],
            // Worked out by the projection over the real rows (`withUsageTotal`): the period's one figure to quote.
            total: { billed_second: true, conversation_count: true, charged_minor: true, currency: true },
        },
    },
    // 2026-10-21: the open workspace's own zone, which every period is counted in (the help host reads it once a turn;
    // the dashboard's "today" and the billing day are this zone's). Every workspace role reaches its route.
    workspace: {
        operationId: "getWorkspace",
        pathTemplate: "/v1/workspaces/{workspace_id}",
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: { data: { workspace_id: true, name: "quoted", time_zone: true, state: true, kind: true } },
    },
    usage_summary: {
        operationId: "getWorkspaceUsageSummary",
        pathTemplate: "/v1/workspace/usage-summary",
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                usage_day: true,
                time_zone: true,
                live_conversation_count: true,
                billed_second_today: true,
                spend_today: AMOUNT,
                answered_first_time_basis_point: true,
                tier: { name: true, channel_unit_ceiling: true },
                channel_unit_in_use: true,
                concurrency: { entitlement: true },
            },
        },
    },
    invoices: {
        operationId: "listInvoices",
        pathTemplate: "/v1/billing/invoices",
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: { limit: LIMIT },
        keep: { data: [{ invoice_id: true, period_month: true, minute_total: true, amount_minor: true, currency: true, issued_at: true }] },
    },
    financial_documents: {
        operationId: "listFinancialDocuments",
        // Joined from segments: the path-helper rule reads a tenant-id placeholder followed by a slash as a storage path.
        pathTemplate: ["/v1/accounts", "{account_id}", "financial-documents"].join("/"),
        wave: "r1",
        requiredId: [],
        period: month,
        fixedQuery: { limit: LIMIT },
        keep: {
            data: [
                {
                    financial_document_id: true,
                    kind: true,
                    document_number: true,
                    document_state: true,
                    payment_state: true,
                    issued_at: true,
                    billing_account_name: "quoted",
                    net_minor: true,
                    tax_minor: true,
                    gross_minor: true,
                    currency: true,
                },
            ],
            estimated: [{ kind: true, document_state: true, billing_account_name: "quoted", billed_second: true, net_minor: true, currency: true }],
        },
    },
    subscription: {
        operationId: "getBalanceSubscription",
        pathTemplate: "/v1/billing-accounts/{billing_account_id}/subscription",
        wave: "r1",
        requiredId: ["billing_account_id"],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                subscription: {
                    state: true,
                    offer: { offer_code: true, display_name: true },
                    billing_period: true,
                    period: { start: true, end: true },
                    current_period: { start: true, end: true, charge_state: true },
                    cancel_at_period_end: true,
                    cancellation_requested_at: true,
                    collection_mode: true,
                    activated_at: true,
                    included_talk_second_granted: true,
                    included_talk_second_used: true,
                    included_talk_second_left: true,
                    extra_talk_second: true,
                    extra_amount_minor: true,
                    currency: true,
                },
            },
        },
    },
    entitlements: {
        operationId: "getEntitlements",
        // Joined from segments: the path-helper rule reads a tenant-id placeholder followed by a slash as a storage path.
        pathTemplate: ["/v1/accounts", "{account_id}", "entitlements"].join("/"),
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                at: true,
                grant: [
                    {
                        kind: true,
                        resource_kind: true,
                        granted_unit: true,
                        reserved_unit: true,
                        consumed_unit: true,
                        unallocated_unit: true,
                        active_from: true,
                        active_until: true,
                        source: true,
                    },
                ],
                concurrency: { entitlement: true },
            },
        },
    },
    own_talk_rates: {
        operationId: "getTalkRates",
        pathTemplate: "/v1/talk-rates",
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                talk_rates: [{ talk_rate_kind: true, talk_rate_minor_per_minute: true, talk_rate_minor_per_hour: true, currency: true, source: true }],
                upcoming_talk_rate_change: [
                    { talk_rate_kind: true, starts_at: true, talk_rate_minor_per_minute: true, talk_rate_minor_per_hour: true, currency: true },
                ],
                currency: true,
            },
        },
    },
    budget: {
        operationId: "getBudget",
        pathTemplate: "/v1/billing/budget",
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: {
            data: { configured: true, month_budget_minor: true, alert_threshold_percent: true, hard_stop_enabled: true, cap_behavior: true, currency: true },
        },
    },
    auto_reload: {
        operationId: "getAutoReloadPolicy",
        pathTemplate: "/v1/billing/auto-reload",
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                configured: true,
                enabled: true,
                threshold_minor: true,
                top_up_minor: true,
                monthly_cap_minor: true,
                month_charged_minor: true,
                currency: true,
                maximum_per_day: true,
                collection_mode: true,
            },
        },
    },
    extra_time: {
        operationId: "getExtraTime",
        pathTemplate: "/v1/account/extra-time",
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: { data: { extra_time_enabled: true, terms_include_minutes: true } },
    },
    // ── VoiceAgents (R1) ──
    voice_agents: {
        operationId: "listVoiceAgents",
        pathTemplate: "/v1/agents",
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: { data: [VOICE_AGENT_SUMMARY] },
    },
    voice_agent: {
        operationId: "getVoiceAgent",
        pathTemplate: "/v1/agents/{voice_agent_id}",
        wave: "r1",
        requiredId: ["voice_agent_id"],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                ...VOICE_AGENT_SUMMARY,
                live_version_id: true,
                online_payment_unavailable: true,
                forward_to_phone: true,
                forward_ring_second: true,
                forward_time_limit_second: true,
            },
        },
    },
    voice_agent_analytics: {
        operationId: "getVoiceAgentAnalytics",
        pathTemplate: "/v1/agents/{voice_agent_id}/analytics",
        wave: "r1",
        requiredId: ["voice_agent_id"],
        period: sinceUntil,
        fixedQuery: {},
        keep: {
            data: {
                voice_agent_id: true,
                time_zone: true,
                since: true,
                until: true,
                success_metric_label: true,
                counts_answered_only: true,
                excludes_test_call: true,
                figure: {
                    conversation_count: true,
                    average_billed_second: true,
                    billed_second_total: true,
                    billed_amount_total: AMOUNT,
                    voice_second_total: true,
                    text_second_total: true,
                    outcome_reached_count: true,
                    transferred_count: true,
                    excluded_test_call_count: true,
                    excluded_unanswered_count: true,
                },
                outcome: [{ outcome: true, conversation_count: true }],
                language: [{ language_code: true, conversation_count: true }],
                by_medium: [{ medium: true, direction: true, conversation_count: true }],
                daily: [{ day: true, conversation_count: true }],
                gap: [{ gap_id: true, question_normalised: "untrusted", caller_count: true, last_seen_at: true }],
                // plan-031: answered outbound conversations and those a voicemail answered.
                outbound: { answered_outbound_count: true, voicemail_count: true },
            },
        },
    },
    gaps: {
        operationId: "listGaps",
        pathTemplate: "/v1/agents/{voice_agent_id}/gaps",
        wave: "r1",
        requiredId: ["voice_agent_id"],
        period: null,
        fixedQuery: { state: "open" },
        keep: {
            data: [
                {
                    gap_id: true,
                    question_normalised: "untrusted",
                    representative_quote: "untrusted",
                    caller_count: true,
                    first_seen_at: true,
                    last_seen_at: true,
                    state: true,
                },
            ],
        },
    },
    conversation_flags: {
        operationId: "listConversationFlags",
        pathTemplate: "/v1/agents/{voice_agent_id}/flags",
        wave: "r1",
        requiredId: ["voice_agent_id"],
        period: null,
        fixedQuery: { state: "open" },
        keep: {
            data: [
                {
                    conversation_flag_id: true,
                    conversation_id: true,
                    severity: true,
                    reason_code: true,
                    raised_by_kind: true,
                    raised_at: true,
                    medium: true,
                    started_at: true,
                    outcome: true,
                },
            ],
        },
    },
    agent_versions: {
        operationId: "listAgentVersions",
        pathTemplate: "/v1/agents/{voice_agent_id}/versions",
        wave: "r1",
        requiredId: ["voice_agent_id"],
        period: null,
        fixedQuery: { limit: "10" },
        keep: {
            data: [
                {
                    agent_version_id: true,
                    label: true,
                    ordinal: true,
                    state: true,
                    note: "quoted",
                    authored_by_name: "quoted",
                    published_by_name: "quoted",
                    language_code: [true],
                    concurrency_ceiling: true,
                    published_at: true,
                    created_at: true,
                    base_is_live: true,
                    base_label: true,
                    draft_source: true,
                },
            ],
        },
    },
    answering: {
        operationId: "getVoiceAgentAnswering",
        pathTemplate: "/v1/agents/{voice_agent_id}/answering",
        wave: "r1",
        requiredId: ["voice_agent_id"],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                published: true,
                time_zone: true,
                opening_hours: true,
                after_hours: true,
                overflow: true,
                overflow_last_7_day: [{ behaviour: true, occurrence: true, longest_wait_ms: true }],
            },
        },
    },
    line_limit: {
        operationId: "getVoiceAgentConcurrency",
        pathTemplate: "/v1/agents/{voice_agent_id}/concurrency",
        wave: "r1",
        requiredId: ["voice_agent_id"],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                in_use: true,
                channel_unit_ceiling: true,
                channel_unit_limit: true,
                version_ceiling: true,
                entitlement_ceiling: true,
                voice_agent_state: true,
            },
        },
    },
    // ── conversations (R1: lists and counts) ──
    conversations: {
        operationId: "listConversations",
        pathTemplate: "/v1/conversations",
        wave: "r1",
        requiredId: [],
        period: sinceUntil,
        fixedQuery: { limit: LIMIT },
        keep: {
            data: [CONVERSATION_SUMMARY],
            figure: { conversation_count: true, answered_count: true, unanswered_count: true, billed_second_total: true, billed_amount_total: AMOUNT },
            // Worked out by the projection (`withListedSpan`): the listed ones' first and last start, and whether all are listed.
            listed: { count: true, oldest_started_at: true, newest_started_at: true, complete: true },
        },
    },
    live_activity: {
        operationId: "listLiveActivity",
        pathTemplate: "/v1/conversations/live-activity",
        wave: "r1",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                live_conversation_count: true,
                conversation: [{ conversation_id: true, medium: true, language_code: true, started_at: true, voice_agent_id: true, voice_agent_name: "quoted", is_test: true }],
            },
        },
    },
    // ── one conversation, numbers, trunks, people, audit, campaigns, orders (R2) ──
    conversation: {
        operationId: "getConversation",
        pathTemplate: "/v1/conversations/{conversation_id}",
        wave: "r2",
        requiredId: ["conversation_id"],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                ...CONVERSATION_SUMMARY,
                voice_agent_stint: [{ ordinal: true, voice_agent_id: true, display_name: "quoted", began_by: true, outcome: true }],
                taken_message: [{ caller_name: "untrusted", contact_kind: true, message_text: "untrusted", created_at: true, withheld: true }],
                handoff: { outcome: true, path: true, summary: "untrusted" },
                transcript_withheld: true,
                // Only as the route includes them (`transcript.read`); cut to HELP_ACCOUNT_TRANSCRIPT_LINE_LIMIT below.
                turn: [{ turn_index: true, speaker: true, text: "untrusted", started_at: true }],
            },
        },
    },
    numbers: {
        operationId: "listNumbers",
        pathTemplate: "/v1/numbers",
        wave: "r2",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: { data: [NUMBER] },
    },
    voice_agent_numbers: {
        operationId: "listVoiceAgentNumbers",
        pathTemplate: "/v1/agents/{voice_agent_id}/numbers",
        wave: "r2",
        requiredId: ["voice_agent_id"],
        period: null,
        fixedQuery: {},
        keep: { data: [NUMBER] },
    },
    sip_trunks: {
        operationId: "listSipTrunks",
        pathTemplate: "/v1/sip-trunks",
        wave: "r2",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: { data: [SIP_TRUNK] },
    },
    sip_trunk: {
        operationId: "getSipTrunk",
        pathTemplate: "/v1/sip-trunks/{sip_trunk_id}",
        wave: "r2",
        requiredId: ["sip_trunk_id"],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                ...SIP_TRUNK,
                number: [{ phone_number_id: true, e164: true, is_caller_id: true, voice_agent_id: true, voice_agent_name: "quoted", binding_enabled: true }],
            },
        },
    },
    account_members: {
        operationId: "listAccountMembers",
        // Joined from segments: the path-helper rule reads a tenant-id placeholder followed by a slash as a storage path.
        pathTemplate: ["/v1/accounts", "{account_id}", "members"].join("/"),
        wave: "r2",
        requiredId: [],
        period: null,
        fixedQuery: {},
        // plan-025 OD-4: names and roles; never an address in a list.
        keep: { data: [{ person: { person_id: true, display_name: "quoted" }, account_role: true, workspace_role: [true], last_seen_at: true }] },
    },
    workspace_members: {
        operationId: "listWorkspaceMembers",
        // Joined from segments: the path-helper rule reads a tenant-id placeholder followed by a slash as a storage path.
        pathTemplate: ["/v1/workspaces", "{workspace_id}", "members"].join("/"),
        wave: "r2",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                member: [{ person_id: true, display_name: "quoted", role: true, joined_at: true, last_active_at: true }],
                invitation: [{ state: true, role: true, expires_at: true, created_at: true }],
            },
        },
    },
    invitations: {
        operationId: "listAccountInvitations",
        // Joined from segments: the path-helper rule reads a tenant-id placeholder followed by a slash as a storage path.
        pathTemplate: ["/v1/accounts", "{account_id}", "invitations"].join("/"),
        wave: "r2",
        requiredId: [],
        period: null,
        fixedQuery: {},
        keep: { data: [{ account_role: true, workspace_role: [true], state: true, expires_at: true, created_at: true, invited_by: { display_name: "quoted" } }] },
    },
    audit_entries: {
        operationId: "listAuditEntries",
        pathTemplate: "/v1/audit-entries",
        wave: "r2",
        requiredId: [],
        period: fromTo,
        fixedQuery: { limit: LIMIT },
        keep: AUDIT_ENTRY,
    },
    account_audit_entries: {
        operationId: "listAccountAuditEntries",
        // Joined from segments: the path-helper rule reads a tenant-id placeholder followed by a slash as a storage path.
        pathTemplate: ["/v1/accounts", "{account_id}", "audit-entries"].join("/"),
        wave: "r2",
        requiredId: [],
        period: fromTo,
        fixedQuery: { limit: LIMIT },
        keep: AUDIT_ENTRY,
    },
    campaigns: {
        operationId: "listCampaigns",
        pathTemplate: "/v1/agents/{voice_agent_id}/campaigns",
        wave: "r2",
        requiredId: ["voice_agent_id"],
        period: null,
        fixedQuery: { limit: LIMIT },
        keep: { data: [CAMPAIGN] },
    },
    campaign: {
        operationId: "getCampaign",
        pathTemplate: "/v1/campaigns/{campaign_id}",
        wave: "r2",
        requiredId: ["campaign_id"],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                ...CAMPAIGN,
                voice_agent_id: true,
                connected_count: true,
                // plan-031: `code` includes `voicemail` (retried like `no_answer`, never connected); the switch says whether
                // voicemails are detected right now (`off` | `shadow` | `on`).
                voicemail_count: true,
                voicemail_detection: true,
                // plan-033: people who rang the campaign's number back (each counted in `outcome` under what happened); `code`
                // names the two hang-ups (`hung_up_mid_way`, `hung_up_before_first_question`) in place of `partial`.
                called_back_count: true,
                outcome: [{ code: true, count: true, share_basis_point: true }],
                // 2026-10-30: `calling_hours` lists every range per weekday; the three fields after it are only their summary.
                schedule: {
                    calling_hours: [{ day_of_week: true, from_local_time: true, to_local_time: true }],
                    day_of_week: [true],
                    call_from_local_time: true,
                    call_to_local_time: true,
                    attempts_per_person: true,
                },
                cost: { spent: AMOUNT, billed_second: true, projected: AMOUNT },
            },
        },
    },
    customer_order_summary: {
        operationId: "summariseCustomerOrders",
        pathTemplate: "/v1/orders/summary",
        wave: "r2",
        requiredId: [],
        period: (window) => ({ since: window.start.toISOString() }),
        fixedQuery: {},
        keep: {
            shop_name: "quoted",
            since: true,
            order_count: true,
            active_count: true,
            delivered_count: true,
            cancelled_count: true,
            gross: "major_money",
            taken: "major_money",
            taken_count: true,
            currency: true,
        },
    },
    customer_orders: {
        operationId: "listCustomerOrders",
        pathTemplate: "/v1/orders",
        wave: "r2",
        requiredId: [],
        period: null,
        fixedQuery: { limit: LIMIT },
        // Never the shop customer's phone, address or notes.
        keep: {
            data: [
                { id: true, status: true, created_at: true, eta_minutes: true, total: "major_money", currency: true, paid: true, payment_state: true, source: true, voice_agent_name: "quoted" },
            ],
        },
    },
    workspaces: {
        operationId: "listWorkspaces",
        pathTemplate: "/v1/workspaces",
        wave: "r2",
        requiredId: [],
        period: null,
        fixedQuery: { limit: LIMIT },
        keep: {
            data: [
                { workspace_id: true, name: "quoted", state: true, kind: true, member_count: true, voice_agent_count: true, month_billed_second: true, month_priced_minor: true, currency: true },
            ],
        },
    },
    notifications: {
        operationId: "listNotifications",
        pathTemplate: "/v1/notifications",
        wave: "r2",
        requiredId: [],
        period: null,
        fixedQuery: { limit: LIMIT },
        keep: { data: [{ kind: true, title: true, body: true, created_at: true, unread: true }], unread_count: true },
    },
    // ── a survey's questions (R2; plan-030 HELP-Q, design §41.11): read and explained, never changed ──
    survey_questionnaire: {
        operationId: "getVoiceAgentQuestionnaireDocument",
        pathTemplate: "/v1/agents/{voice_agent_id}/questionnaire-document",
        wave: "r2",
        requiredId: ["voice_agent_id"],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                agent_version_id: true,
                agent_version_label: true,
                is_draft: true,
                base_agent_version_id: true,
                editable: true,
                not_editable_reason: true,
                format: true,
                campaign_dialling: [{ campaign_id: true, name: "quoted", state: true }],
                // Worked out by the projection (`withQuestionnaireOutline`) from the route's per-language `survey` files.
                questionnaire: [
                    {
                        language_code: true,
                        group_count: true,
                        question: [
                            {
                                question_code: true,
                                question_type: true,
                                group: [true],
                                text: "quoted_untrusted",
                                option_label: ["quoted_untrusted"],
                                selection_min: true,
                                selection_max: true,
                                min: true,
                                max: true,
                                integer: true,
                                unit: "quoted_untrusted",
                            },
                        ],
                    },
                ],
            },
        },
    },
    // ── call reviews (R2; plan-032 HELP-OR, design §43.14; OD-8): read and explained, never started, accepted or published ──
    optimisation_reviews: {
        operationId: "listOptimisationReviews",
        pathTemplate: "/v1/agents/{voice_agent_id}/optimisation-reviews",
        wave: "r2",
        requiredId: ["voice_agent_id"],
        period: null,
        fixedQuery: { limit: "10" },
        keep: {
            data: [
                {
                    optimisation_review_id: true,
                    depth: true,
                    scope: true,
                    state: true,
                    failure_reason: true,
                    finding_count: true,
                    requested_at: true,
                    finished_at: true,
                    published_agent_version_id: true,
                },
            ],
        },
    },
    optimisation_review: {
        operationId: "getOptimisationReview",
        pathTemplate: "/v1/optimisation-reviews/{optimisation_review_id}",
        wave: "r2",
        requiredId: ["optimisation_review_id"],
        period: null,
        fixedQuery: {},
        keep: {
            data: {
                optimisation_review_id: true,
                voice_agent_id: true,
                depth: true,
                scope: true,
                state: true,
                failure_reason: true,
                conversation_count: true,
                conversation_read_count: true,
                billing: { payer: true },
                accepted_count: true,
                next_agent_version_label: true,
                requested_at: true,
                started_at: true,
                finished_at: true,
                published_agent_version_id: true,
                // Written by the review from callers' words: kept, and the result is `untrusted_text`. Never `evidence_line`.
                finding: [
                    {
                        optimisation_finding_id: true,
                        position: true,
                        category: true,
                        fix_kind: true,
                        advice_target: true,
                        issue: "untrusted",
                        evidence: "untrusted",
                        proposed_fix_text: "untrusted",
                        affected_conversation_count: true,
                        decision: true,
                    },
                ],
            },
        },
    },
});
/** Every read code, in registry order. */
export const HELP_ACCOUNT_READ_CODE = Object.freeze(Object.keys(HELP_ACCOUNT_READ));
/** Whether a value is a read code. */
export function isHelpAccountReadCode(value) {
    return typeof value === "string" && Object.hasOwn(HELP_ACCOUNT_READ, value);
}
/** The read codes offered while `waves` are switched on (R2 implies R1). */
export function helpAccountReadCodesOf(wave) {
    return HELP_ACCOUNT_READ_CODE.filter((code) => wave === "r2" || HELP_ACCOUNT_READ[code].wave === "r1");
}
/**
 * The customer `GET` operations the help assistant never reads, whatever is asked (design §36.5): keys, webhooks, payment
 * methods, payouts, security, SIP credentials, recordings, exports and downloads, other people's numbers, connections,
 * website text, previews, help requests. The arch rule `help-account-registry` holds this list and the registry apart.
 */
export const HELP_ACCOUNT_NEVER_READ = Object.freeze([
    "listApiKeys",
    "listWorkspaceApiKeys",
    "listAccountApiKeys",
    "listWebhookEndpoints",
    "listWebhookDeliveryAttempts",
    "listBillingPaymentMethods",
    "readPayoutDestination",
    "readPayoutStatement",
    "exportPayoutStatement",
    "getAccountSecurity",
    "readMyProfile",
    "listMySignInMethods",
    "listMyAuthSessions",
    "listSipCredentials",
    "getVoiceAgentSip",
    "downloadFinancialDocument",
    "exportContactListCsv",
    "exportCustomerOrdersCsv",
    "listContactLists",
    "getContactList",
    "listContactListContacts",
    "listDoNotCallEntries",
    "listConnections",
    "getConnection",
    "getWebsiteReading",
    "getWebsiteReadingPage",
    "readDocumentPreview",
    "previewRetentionPolicy",
    "listHelpRequests",
    "getHelpRequest",
    "getCurrentAuthSession",
    // plan-030 HELP-Q (design §41.11): a survey's questions are read through `survey_questionnaire`, never downloaded.
    "getVoiceAgentQuestionnaireFile",
]);
/** EVAL-FIX round 2: the list whose result carries each id a read may need. */
export const HELP_ACCOUNT_READ_ID_SOURCE = Object.freeze({
    voice_agent_id: "voice_agents",
    billing_account_id: "balance",
    conversation_id: "conversations",
    sip_trunk_id: "sip_trunks",
    campaign_id: "campaigns",
    workspace_member_person_id: "workspace_members",
    optimisation_review_id: "optimisation_reviews",
});
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
/**
 * Parses `read_account`'s argument JSON: an unknown key or a wrong shape is refused, a missing id is `id_missing`; an id,
 * a period or a date the read does not take is left out (EVAL-FIX round 2).
 */
export function parseHelpAccountReadArgument(argumentJson) {
    let value;
    try {
        value = JSON.parse(argumentJson);
    }
    catch {
        return "argument_invalid";
    }
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return "argument_invalid";
    const f = value;
    const read = f["read"];
    if (!isHelpAccountReadCode(read))
        return "read_unknown";
    const entry = HELP_ACCOUNT_READ[read];
    const id = {};
    let period = null;
    let date = null;
    /** Ids given under a name the read does not take. */
    const unused = [];
    for (const [key, given] of Object.entries(f)) {
        if (key === "read")
            continue;
        if (key === "period") {
            if (given === null || given === undefined)
                continue;
            if (typeof given !== "string" || !HELP_ACCOUNT_READ_PERIOD.includes(given))
                return "argument_invalid";
            // EVAL-FIX round 2: a period on a read that takes none is left out, never a refusal (the read is the same).
            if (entry.period !== null)
                period = given;
            continue;
        }
        if (key === "date") {
            if (given === null || given === undefined)
                continue;
            if (!isHelpCalendarDate(given))
                return "argument_invalid";
            if (entry.period !== null)
                date = given;
            continue;
        }
        if (!HELP_ACCOUNT_READ_ID_ARGUMENT.includes(key))
            return "argument_invalid";
        if (given === null || given === undefined)
            continue;
        // EVAL-FIX round 2: an id the read does not take is left out, never a refusal — the request is built from the
        // registry alone, so an extra id can widen nothing (the first backup runs refused `conversations` with a
        // voice_agent_id and `gaps` with a period, and the model then said it could not check the account).
        if (!entry.requiredId.includes(key)) {
            if (typeof given === "string" && UUID_SHAPE.test(given.toLowerCase()))
                unused.push(given.toLowerCase());
            continue;
        }
        if (typeof given !== "string" || !UUID_SHAPE.test(given.toLowerCase()))
            return "argument_invalid";
        id[key] = given.toLowerCase();
    }
    // 2026-10-21 (the R2 evaluation: `campaigns` asked twice with the agent's id as `campaign_id`, then given up): ONE id
    // under the wrong name, when exactly ONE id is missing, is taken as that one. It widens nothing — the route's own
    // checks decide, under the person's permissions, and a wrong id is the route's 404.
    const missing = entry.requiredId.filter((key) => id[key] === undefined);
    if (missing.length === 1 && unused.length === 1)
        id[missing[0]] = unused[0];
    if (entry.requiredId.some((key) => id[key] === undefined))
        return "id_missing";
    return { read, id, period: date === null ? period : null, date };
}
/**
 * The route's concrete path and query for a parsed read; `workspace_required` when the route needs a workspace; and
 * (2026-10-21) `time_zone_required` when the read has a period or a date and the workspace's zone is not known — a
 * period is never counted in UTC by default.
 */
export function helpAccountReadRequestOf(argument, scope) {
    const entry = HELP_ACCOUNT_READ[argument.read];
    let path = entry.pathTemplate.replace("{account_id}", scope.accountId);
    if (path.includes("{workspace_id}")) {
        if (scope.workspaceId === null)
            return "workspace_required";
        path = path.replace("{workspace_id}", scope.workspaceId);
    }
    for (const key of entry.requiredId) {
        const given = argument.id[key];
        if (given === undefined)
            throw new Error(`read ${argument.read}: ${key} was not parsed`);
        path = path.replace(`{${key}}`, given);
    }
    if (/\{[a-z_]+\}/u.test(path))
        throw new Error(`read ${argument.read}: the path template ${entry.pathTemplate} names a parameter no argument fills`);
    const query = { ...entry.fixedQuery };
    if (entry.period === null || (argument.period === null && argument.date === null))
        return { path, query };
    if (!isHelpTimeZone(scope.timeZone))
        return "time_zone_required";
    const window = helpAccountReadWindowOf(argument.date !== null ? { date: argument.date } : { period: argument.period }, scope.nowMs, scope.timeZone);
    Object.assign(query, entry.period(window));
    return { path, query, window };
}
/**
 * Review S5: whether a delegated request's query is one {@link helpAccountReadRequestOf} could have built for `code` —
 * the entry's fixed parameters with their very values, and the period's own parameters, nothing else. The api refuses
 * any other query, so a delegation never widens a read by a parameter the help host would not send.
 */
export function helpAccountReadQueryAllowed(code, query) {
    const entry = HELP_ACCOUNT_READ[code];
    const periodKey = entry.period === null ? [] : Object.keys(entry.period(helpAccountReadWindowOf({ period: "last_7_days" }, Date.UTC(2026, 0, 15), "UTC")));
    for (const [key, value] of Object.entries(query)) {
        const fixed = entry.fixedQuery[key];
        if (fixed !== undefined) {
            if (fixed !== value)
                return false;
            continue;
        }
        if (!periodKey.includes(key))
            return false;
    }
    return true;
}
/** Whether `path` (a concrete request path) is an instance of the read's path template. */
export function helpAccountReadPathMatches(code, path) {
    const pattern = HELP_ACCOUNT_READ[code].pathTemplate.split("/").map((segment) => segment.startsWith("{") ? "[^/]+" : segment.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&"));
    return new RegExp(`^${pattern.join("/")}$`, "u").test(path);
}
function textOf(value) {
    const scrubbed = scrubSecretShape(value).text;
    return scrubbed.length <= HELP_ACCOUNT_READ_TEXT_CHAR_LIMIT ? scrubbed : `${scrubbed.slice(0, HELP_ACCOUNT_READ_TEXT_CHAR_LIMIT)}…`;
}
const CURRENCY_SHAPE = /^[A-Z]{3}$/u;
function moneyText(minor, currency) {
    if (!Number.isSafeInteger(minor) || !CURRENCY_SHAPE.test(currency))
        return null;
    return formatCreditMoney(minor, currency);
}
/** A scalar the projection keeps: strings scrubbed and cut; numbers, booleans and null as they are; anything else dropped. */
function scalarOf(value) {
    if (typeof value === "string")
        return textOf(value);
    if (typeof value === "number" || typeof value === "boolean" || value === null)
        return value;
    return undefined;
}
/**
 * `true` on a key whose value is an object or a list keeps it only as JSON scalars nested at most two levels (the
 * answering hours' shapes): never a deeper structure the projection did not name.
 */
function plainOf(value, depth) {
    if (Array.isArray(value))
        return depth <= 0 ? undefined : value.slice(0, HELP_ACCOUNT_READ_LIST_LIMIT).map((one) => plainOf(one, depth - 1));
    if (typeof value === "object" && value !== null) {
        if (depth <= 0)
            return undefined;
        const out = {};
        for (const [key, one] of Object.entries(value)) {
            const kept = plainOf(one, depth - 1);
            if (kept !== undefined)
                out[key] = kept;
        }
        return out;
    }
    return scalarOf(value);
}
function project(value, keep, path, walk, currency, listLimit) {
    if (keep === true)
        return plainOf(value, 2);
    if (keep === "quoted" || keep === "untrusted" || keep === "quoted_untrusted") {
        if (typeof value !== "string")
            return scalarOf(value);
        if (keep !== "untrusted")
            walk.quoted.add(path.replace(/\.\d+(?=\.|$)/gu, "[]"));
        if (keep !== "quoted" && value.trim() !== "")
            walk.untrusted = true;
        return textOf(value);
    }
    if (keep === "major_money")
        return typeof value === "number" && Number.isFinite(value) ? value : undefined;
    if (Array.isArray(keep)) {
        if (!Array.isArray(value))
            return undefined;
        const [itemKeep] = keep;
        if (value.length > listLimit)
            walk.more[path] = value.length - listLimit;
        return value.slice(0, listLimit).map((one, index) => project(one, itemKeep, `${path}.${String(index)}`, walk, currency, listLimit));
    }
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return value === null ? null : undefined;
    const object = value;
    const own = typeof object["currency"] === "string" ? object["currency"] : currency;
    const out = {};
    for (const [key, keepOne] of Object.entries(keep)) {
        if (!Object.hasOwn(object, key))
            continue;
        const kept = project(object[key], keepOne, path === "" ? key : `${path}.${key}`, walk, own, listLimit);
        if (kept === undefined)
            continue;
        out[key] = kept;
        // Money as the screens print it, beside the route's own minor units: `available_minor` → `available_text`.
        if (key.endsWith("_minor") && typeof kept === "number" && own !== null) {
            const text = moneyText(kept, own);
            if (text !== null)
                out[`${key.slice(0, -"_minor".length)}_text`] = text;
        }
        if (key === "amount_minor" && typeof kept === "number" && own !== null) {
            const text = moneyText(kept, own);
            if (text !== null)
                out["amount_text"] = text;
        }
        if (keepOne === "major_money" && typeof kept === "number" && own !== null) {
            const text = moneyText(Math.round(kept * 100), own);
            if (text !== null)
                out[`${key}_text`] = text;
        }
    }
    return out;
}
/** The transcript is cut to its last lines: the newest are what a person asks about. */
function withTranscriptCut(body) {
    if (typeof body !== "object" || body === null)
        return body;
    const data = body.data;
    if (typeof data !== "object" || data === null || !Array.isArray(data.turn))
        return body;
    const turn = data.turn;
    if (turn.length <= HELP_ACCOUNT_TRANSCRIPT_LINE_LIMIT)
        return body;
    return { ...body, data: { ...data, turn: turn.slice(turn.length - HELP_ACCOUNT_TRANSCRIPT_LINE_LIMIT), turn_earlier_count: turn.length - HELP_ACCOUNT_TRANSCRIPT_LINE_LIMIT } };
}
/**
 * plan-025 (2026-10-21): the conversations' figure gains `unanswered_count` — `conversation_count − answered_count`, the
 * ones that rang out, queued or were refused — worked out HERE, so "how many calls were missed" never asks the model
 * for a difference (it is told to work out none). Only when both figures are whole numbers.
 */
/**
 * 2026-10-21 (the R2 evaluation: a Greek "how much talk time this week" answered without its charge, twice): the usage
 * rows gain a `total` over the real (not test) rows — talk seconds, conversations and the charge in minor units, with
 * its `charged_text` — worked out HERE, before the list is cut, so the answer quotes one figure and the model adds
 * nothing. Only when every row is whole numbers in one currency.
 */
function withUsageTotal(body) {
    if (typeof body !== "object" || body === null)
        return body;
    const data = body.data;
    if (!Array.isArray(data))
        return body;
    let billedSecond = 0;
    let conversationCount = 0;
    let chargedMinor = 0;
    const currency = new Set();
    for (const one of data) {
        if (typeof one !== "object" || one === null || one["is_test"] === true)
            continue;
        const [billed, counted, charged, own] = [one["billed_second"], one["conversation_count"], one["charged_minor"], one["currency"]];
        if (!Number.isSafeInteger(billed) || !Number.isSafeInteger(counted) || !Number.isSafeInteger(charged) || typeof own !== "string")
            return body;
        billedSecond += billed;
        conversationCount += counted;
        chargedMinor += charged;
        currency.add(own);
    }
    if (currency.size !== 1)
        return body;
    return { ...body, total: { billed_second: billedSecond, conversation_count: conversationCount, charged_minor: chargedMinor, currency: [...currency][0] } };
}
/**
 * 2026-10-22 (the local-days evaluation: "when did the first call come in today?" answered with the NEWEST call's time,
 * the list being newest first): the listed conversations' oldest and newest start, worked out here, and whether the
 * list holds the whole filtered set (`complete`: the figure counts no more than are listed). The help host adds each
 * instant's local time, so the first call of a day is one value to quote, never a list to sort.
 */
function withListedSpan(body) {
    if (typeof body !== "object" || body === null)
        return body;
    const data = body.data;
    if (!Array.isArray(data))
        return body;
    const started = data
        .map((one) => (typeof one === "object" && one !== null ? one["started_at"] : undefined))
        .filter((one) => typeof one === "string" && Number.isFinite(Date.parse(one)))
        .sort((left, right) => Date.parse(left) - Date.parse(right));
    if (started.length === 0)
        return body;
    const total = (body.figure ?? {}).conversation_count;
    return {
        ...body,
        listed: {
            count: started.length,
            oldest_started_at: started[0],
            newest_started_at: started.at(-1),
            complete: Number.isSafeInteger(total) && total <= data.length,
        },
    };
}
function withUnansweredCount(body) {
    if (typeof body !== "object" || body === null)
        return body;
    const figure = body.figure;
    if (typeof figure !== "object" || figure === null)
        return body;
    const { conversation_count: total, answered_count: answered } = figure;
    if (!Number.isSafeInteger(total) || !Number.isSafeInteger(answered))
        return body;
    return { ...body, figure: { ...figure, unanswered_count: Math.max(0, total - answered) } };
}
function recordOf(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? value : null;
}
/** One question as the questionnaire read keeps it: its code, type, wording, options' labels and range. */
function questionOutlineOf(question, group) {
    const out = { question_code: question["id"], question_type: question["type"], group, text: question["text"] };
    if (Array.isArray(question["options"])) {
        out["option_label"] = question["options"].map((option) => recordOf(option)?.["label"]).filter((label) => typeof label === "string");
    }
    const selection = recordOf(question["selection"]);
    if (selection !== null) {
        out["selection_min"] = selection["min"];
        out["selection_max"] = selection["max"];
    }
    if (question["type"] === "number")
        for (const key of ["min", "max", "integer", "unit"])
            if (question[key] !== undefined)
                out[key] = question[key];
    return out;
}
/**
 * plan-030 HELP-Q (design §41.11): the questionnaire read's outline, worked out HERE from the route's `survey` — one
 * questionnaire file per language code, a map the projection's `keep` cannot name — as `questionnaire`: per language,
 * the number of question groups and each question once, in group A's order then the questions only group B asks, with
 * the groups that ask it. Only the questions: never the script around them (greetings, replies, closing), which the
 * projection then leaves out with the code history, the pack's words and wetalk's own lines.
 */
function withQuestionnaireOutline(body) {
    const data = recordOf(recordOf(body)?.["data"]);
    const survey = recordOf(data?.["survey"]);
    if (data === null || survey === null)
        return body;
    const questionnaire = Object.entries(survey).map(([languageCode, file]) => {
        const variant = recordOf(recordOf(file)?.["variants"]) ?? {};
        const groupName = Object.keys(variant).filter((name) => Array.isArray(recordOf(variant[name])?.["questions"]));
        const byCode = new Map();
        const question = [];
        for (const name of groupName) {
            const listed = recordOf(variant[name])?.["questions"];
            if (!Array.isArray(listed))
                continue;
            for (const one of listed) {
                const record = recordOf(one);
                if (record === null || typeof record["id"] !== "string")
                    continue;
                const held = byCode.get(record["id"]);
                if (held !== undefined) {
                    held.push(name);
                    continue;
                }
                const group = [name];
                byCode.set(record["id"], group);
                question.push(questionOutlineOf(record, group));
            }
        }
        return { language_code: languageCode, group_count: groupName.length, question };
    });
    return { ...body, data: { ...data, questionnaire } };
}
/**
 * The projection of a route's 2xx answer for `code` (design §36.6). Pure. When the result is longer than
 * `HELP_ACCOUNT_READ_RESULT_CHAR_LIMIT`, list items are left out from the end, longest list first, and counted in `more`.
 */
export function projectHelpAccountRead(code, body) {
    const entry = HELP_ACCOUNT_READ[code];
    const source = code === "conversation"
        ? withTranscriptCut(body)
        : code === "conversations"
            ? withListedSpan(withUnansweredCount(body))
            : code === "usage"
                ? withUsageTotal(body)
                : code === "survey_questionnaire"
                    ? withQuestionnaireOutline(body)
                    : body;
    let listLimit = HELP_ACCOUNT_READ_LIST_LIMIT;
    for (;;) {
        const walk = { more: {}, quoted: new Set(), untrusted: false };
        const projected = project(source, entry.keep, "", walk, null, listLimit);
        const data = typeof projected === "object" && projected !== null && !Array.isArray(projected) ? projected : {};
        const turnEarlier = source?.data?.turn_earlier_count;
        const more = { ...walk.more };
        if (typeof turnEarlier === "number")
            more["data.turn"] = (more["data.turn"] ?? 0) + turnEarlier;
        const result = {
            result: "read",
            read: code,
            data,
            more,
            quoted_field: [...walk.quoted].sort(),
            untrusted_text: walk.untrusted,
        };
        if (JSON.stringify(result).length <= HELP_ACCOUNT_READ_RESULT_CHAR_LIMIT || listLimit <= 1)
            return result;
        listLimit = Math.max(1, Math.floor(listLimit / 2));
    }
}
/**
 * The wave of reads offered now (plan-025 OD-2): `r1` until R2's evaluation groups pass; changing it is a runtime deploy,
 * never configuration. `r2` since 2026-10-21 (the owner, testing the live portal, asked what a conversation was about:
 * one conversation, numbers, trunks, people, audit, campaigns and orders). The api checks the same constant (review S5,
 * `apps/api/main.ts`), so the api and the runtime are deployed together for it.
 */
export const HELP_ACCOUNT_READ_ACTIVE_WAVE = "r2";
//# sourceMappingURL=account-read.js.map